import React, { useEffect, useRef, useState } from 'react';
import sharedStyles from '../styles.module.scss';
import Button from '@components-clean/core/button';
import { Popup } from '@components-clean/core/popup/popup';
import { StudyDataFilter } from '@components-clean/core/study-data-filter';
import {
    ChipData,
    FilterCategory,
    FilterUIConfigType,
    StudyDataFilterHandle,
} from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import Tooltip from '@components-clean/core/tooltip';
import {
    useEmitFilterChanged,
    useFilterHttpRequestQueue,
    useStudyDataFilterContext,
} from '@contexts-clean/study-data-filter-context';
import { FilterUIAction } from '@interfaces/central-monitoring';
import { FilterChips } from '@pages-clean/central-monitoring/components/filter-actions/filter-chips';
import {
    MapCalculateFiltersToChips,
    MapConfigToFilterCategories,
} from '@utils-clean/study-data-filter/study-data-filter.service';
import { useTranslation } from '@hooks/index';
import { useGetFilterUIConfig, usePerformFilterUIAction } from '@apis/bff-api/central-monitoring';

type IFilterButtonProps = {
    studyRef: string;
    datasetId?: number;
    anchorClass?: string;
    dataViewConfigId?: string;
    type?: FilterUIConfigType;
};

export const FilterActions = ({ studyRef, datasetId, dataViewConfigId, anchorClass, type }: IFilterButtonProps) => {
    const { setSessionId, sessionId, isFiltered, setIsFiltered, setChipData, preAppliedFilters, copySessionId } =
        useStudyDataFilterContext();
    const { emitFilterChangedEvent } = useEmitFilterChanged();
    const httpRequestQueue$ = useFilterHttpRequestQueue();
    const anchor = useRef<HTMLDivElement | null>(null);
    const [show, setShow] = useState(false);
    const [categories, setCategories] = useState<Array<FilterCategory>>([]);
    const [resetFiltersDisabled, setResetFiltersDisabled] = useState(false);
    const [selectedChip, setSelectedChip] = useState<ChipData | null>(null);
    const filterUI = useRef<StudyDataFilterHandle | null>(null);
    const filterChipElements = useRef<Map<number, HTMLElement>>(new Map());

    const { t } = useTranslation();

    const {
        data,
        isMutating: configLoading,
        trigger: getConfig,
    } = useGetFilterUIConfig(studyRef, dataViewConfigId, false, sessionId, type, copySessionId);
    const { trigger: performFilterValueAction } = usePerformFilterUIAction(
        studyRef,
        sessionId,
        datasetId,
        dataViewConfigId,
    );

    useEffect(() => {
        getConfig(preAppliedFilters)
            .then((x) => {
                const filterCategories = MapConfigToFilterCategories(x.categoryConfig ?? []);
                setCategories(filterCategories);
                setSessionId(x.sessionId);

                setIsFiltered(
                    (preAppliedFilters && preAppliedFilters.filters.length > 0) || !!x.filteredFields?.length,
                );
                setChipData(MapCalculateFiltersToChips(x, filterCategories));
            })
            .catch((e) => window.console.error(e));
    }, [studyRef, dataViewConfigId]);

    const onFilterClick = () => {
        if (!show) setShow(true);
    };

    const onApply = () => {
        setShow(false);
        setSelectedChip(null);
    };

    const onResetFiltersClick = () => {
        setResetFiltersDisabled(true);
        setShow(false);
        httpRequestQueue$.next(() =>
            performFilterValueAction({ action: FilterUIAction.ClearAndApply })
                .then(() => {
                    setIsFiltered(false);
                    setChipData([]);
                    emitFilterChangedEvent();
                })
                .catch((err) => {
                    window.console.error(err);
                })
                .finally(() => setResetFiltersDisabled(false)),
        );
    };

    const onFilterChipClick = (chip: ChipData) => {
        setShow(true);
        if (filterUI.current) filterUI.current?.setSelectedCategory(chip.parentCategory, chip.categoryId);
        else setSelectedChip(chip);
    };

    return (
        <div className="k-d-flex k-gap-1 k-flex-auto" style={{ minWidth: '0' }}>
            <div ref={anchor} className={anchorClass}>
                <Button
                    data-testid="filter-btn"
                    alternativeStyle
                    primary
                    faIcon={['far', 'filter']}
                    faIconStyle={{ width: 16, height: 16 }}
                    disabled={configLoading}
                    onClick={onFilterClick}
                    className={sharedStyles.actionBarButtons}
                    size="small"
                />
            </div>
            <Tooltip parentTitle position="bottom">
                <Button
                    data-testid="clear-filter-btn"
                    alternativeStyle
                    faIcon={['far', 'filter-slash']}
                    className={sharedStyles.actionBarButtons}
                    faIconStyle={{ width: 20, height: 16 }}
                    size="small"
                    disabled={!sessionId || configLoading || !isFiltered || resetFiltersDisabled}
                    onClick={onResetFiltersClick}
                    themeColor="secondary"
                    fillMode="outline"
                    title={t('clearFilters')}
                />
            </Tooltip>
            <FilterChips onClick={onFilterChipClick} filterChipElements={filterChipElements} />
            {!configLoading && data && (
                <Popup
                    data-testid="study-filter-popup"
                    anchor={anchor?.current}
                    show={show}
                    border="topBorder"
                    alternativeStyle
                    closeOnBlur={false}
                    style={{
                        marginTop: '6px',
                    }}
                    popupCard
                >
                    <StudyDataFilter
                        data-testid="study-filter-id"
                        categories={categories}
                        onApply={onApply}
                        datasetId={type === FilterUIConfigType.StudyDashboard ? undefined : datasetId}
                        studyRef={studyRef}
                        onClose={() => {
                            setSelectedChip(null);
                            setShow(false);
                        }}
                        dataViewRef={dataViewConfigId}
                        defaultCategory={selectedChip}
                        ref={filterUI}
                        filterChipElements={filterChipElements}
                    />
                </Popup>
            )}
        </div>
    );
};
